import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useItem } from '../api/queries'
import type { PlantStats, PlantsOverview } from '../api/types'
import { BandPills, HBarList, HatchDefs, StackedAreaBars } from '../components/charts'
import { DateRangeFilter } from '../components/DateRangeFilter'
import { BandBadge, Card, EmptyState, ErrorState, KpiCard, LoadingState, PageHeader, Segmented, cx } from '../components/ui'
import { plantKpis, plantPath } from '../features/dashboard/drilldown'
import type { DashboardFilters } from '../features/dashboard/models'
import { useDashboardFilters } from '../features/dashboard/useDashboardFilters'
import { BAND_ORDER, bandMeta, formatScore } from '../lib/bands'
import { formatDate } from '../lib/format'

function MixBar({ plant }: { plant: PlantStats }) {
  if (plant.total === 0) return null
  return (
    <span className="mixbar" aria-hidden="true">
      {BAND_ORDER.map((b) => {
        const n = plant.counts[b]
        return n ? <span key={b} className={cx('mixbar__seg', b === 'not_assessed' && 'mixbar__seg--na')} style={{ flexGrow: n, background: b === 'not_assessed' ? undefined : bandMeta(b).color }} /> : null
      })}
    </span>
  )
}

function PlantCard({ plant, filters }: { plant: PlantStats; filters: DashboardFilters }) {
  return (
    <li>
      <Link className="plant-card" to={plantPath(plant.plant, filters)} style={{ ['--tile-color' as string]: bandMeta(plant.worst_band).color }}>
        <span className="plant-card__head">
          <span>
            <span className="plant-card__name">{plant.name}</span>
            <span className="plant-card__code mono muted">{plant.code} · {plant.areas} {plant.areas === 1 ? 'área' : 'áreas'}</span>
          </span>
          {plant.total ? <BandBadge band={plant.worst_band} size="sm" /> : null}
        </span>
        <span className="plant-card__stats">
          <span><b className="mono">{plant.total}</b> equipos</span>
          <span><b className="mono">{plant.inspected}</b> con inspección</span>
          <span><b className="mono">{formatScore(plant.avg_score)}</b> puntaje prom.</span>
          <span><b className="mono">{plant.critical}</b> {plant.critical === 1 ? 'crítico' : 'críticos'}</span>
          <span><b className="mono">{plant.open_work_orders}</b> OT abiertas</span>
        </span>
        <MixBar plant={plant} />
        <span className="plant-card__foot muted">
          {plant.latest_inspection_on ? <>Última inspección: <b>{formatDate(plant.latest_inspection_on)}</b></> : 'Sin inspecciones'}
        </span>
      </Link>
    </li>
  )
}

function Overview({ data, filters }: { data: PlantsOverview; filters: DashboardFilters }) {
  const navigate = useNavigate()
  const [barMode, setBarMode] = useState<'count' | 'share'>('count')
  const [bandFilter, setBandFilter] = useState('all')
  if (data.plants.length === 0) return <EmptyState title="Aún no hay plantas" hint="Las plantas se crean al importar sus fichas o desde la administración." />
  const rows = data.plants.map((p) => ({ area: p.plant, code: p.code, name: p.name, total: p.total, ...p.counts }))
  const scores = data.plants.map((p) => (p.avg_score === null ? null : Number(p.avg_score)))
  const max = Math.max(0, ...scores.filter((v): v is number => v !== null))
  const bandCounts = Object.fromEntries(BAND_ORDER.map((b) => [b, data.plants.reduce((sum, p) => sum + p.counts[b], 0)]))
  const open = (plant: number) => navigate(plantPath(plant, filters))
  return (
    <>
      <HatchDefs />
      <section className="kpi-row" aria-label="Cifras clave">
        {plantKpis(data.totals).map((k) => <KpiCard key={k.key} label={k.label} value={k.value} caption={k.caption} tone={k.tone} />)}
      </section>
      <Card title="Plantas">
        <ul className="plant-grid" aria-label="Plantas">
          {data.plants.map((p) => <PlantCard key={p.plant} plant={p} filters={filters} />)}
        </ul>
      </Card>
      <div className="grid grid--2">
        <Card title="Equipos por planta" actions={<Segmented small label="Mostrar" value={barMode} onChange={setBarMode} options={[{ value: 'count', label: 'Cantidad' }, { value: 'share', label: 'Proporción' }]} />}
          footer={<BandPills value={bandFilter} onChange={setBandFilter} counts={bandCounts} />}>
          <StackedAreaBars rows={rows} mode={barMode} bandFilter={bandFilter} onSelectArea={open} caption="Equipos por banda en cada planta" groupLabel="Planta" />
        </Card>
        <Card title="Puntaje promedio por planta">
          <HBarList empty="Aún no hay puntajes" items={data.plants.map((p, i) => ({
            key: p.plant, label: p.name, value: formatScore(p.avg_score),
            widthPct: scores[i] === null || max === 0 ? 0 : Math.round((100 * scores[i]!) / max), tone: bandMeta(p.worst_band).color,
          }))} />
        </Card>
      </div>
    </>
  )
}

/** Panel, macro level: every plant at a glance; a plant opens its areas. */
export function PlantsOverviewPage() {
  const { filters, set } = useDashboardFilters()
  const overview = useItem<PlantsOverview>('/api/dashboard/plants/', { date_from: filters.dateFrom, date_to: filters.dateTo })
  return (
    <div className="page">
      <PageHeader
        title="Panel"
        subtitle={filters.dateTo ? `Todas las plantas: inspección más reciente de cada equipo hasta el ${formatDate(filters.dateTo)}` : 'Todas las plantas: inspección más reciente de cada equipo'}
        actions={
          <div className="filters">
            <DateRangeFilter value={{ from: filters.dateFrom, to: filters.dateTo }} onChange={(r) => set({ ...filters, dateFrom: r.from, dateTo: r.to })} />
          </div>
        }
      />
      {overview.isPending ? <LoadingState label="Cargando las plantas…" /> : overview.isError ? <ErrorState error={overview.error} onRetry={() => void overview.refetch()} title="No se pudo cargar el panel" /> : <Overview data={overview.data} filters={filters} />}
    </div>
  )
}
